import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import Popover from "../../../../Popover";
import { defaultPropArray, defaultPropObject } from "../../../../constants";
import { useMediaQuery, useWindowSize } from "../../../../hooks";
import { CloseLinear, DotsHorizontal, S7Circle, S7ProfiCircle } from "../../../../icons";
import { ButtonBase } from "../../../../internal/ButtonBase";
import { Markup } from "../../../../internal/Markup";
import { getMethodsForTestId } from "../../../../utils/getMethodsForTestId";
import { isFunc } from "../../../../utils/type";
import { isAnchorEvent, isButtonEvent } from '../../guards';
import { DropList } from './DropList';
import styles from './NavLinks.css';
var cx = classnames.bind(styles);
var initialMarkerPosition = {
  left: 0,
  width: 0
};
export var NavLinks = function NavLinks(_ref) {
  var activeItem = _ref.activeItem,
      _ref$items = _ref.items,
      items = _ref$items === void 0 ? defaultPropArray : _ref$items,
      onItemClick = _ref.onItemClick,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'b2c' : _ref$theme,
      namespace = _ref.namespace,
      searchOpen = _ref.searchOpen,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpenMore = _useState2[0],
      setIsOpenMore = _useState2[1];

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'navLinks'),
      getTestId = _getMethodsForTestId.getTestId;

  var windowSize = useWindowSize();
  var activeLinkRef = useRef(null);

  var _useState3 = useState(initialMarkerPosition),
      _useState4 = _slicedToArray(_useState3, 2),
      markerPosition = _useState4[0],
      setMarkerPosition = _useState4[1];

  var breakpoints = useMediaQuery('mdMax');
  var isTablet = breakpoints.mdMax;

  var indicatorPosition = function indicatorPosition(_ref2) {
    var offsetWidth = _ref2.offsetWidth,
        offsetLeft = _ref2.offsetLeft;
    setMarkerPosition({
      left: offsetLeft,
      width: offsetWidth
    });
  };

  useEffect(function () {
    if (activeLinkRef.current) {
      indicatorPosition(activeLinkRef.current);
    } else {
      setMarkerPosition({
        left: 0,
        width: 0
      });
    }
  }, [windowSize, activeItem]);
  var indexedItems = useMemo(function () {
    return items.map(function (item, idx) {
      return {
        index: idx,
        title: item.title,
        titleProps: item.titleProps,
        href: item.href,
        as: item.as,
        isDropDown: item.isDropDown,
        items: item.items,
        active: item.active
      };
    });
  }, [items]);
  var navigationItems = isTablet ? indexedItems.slice(0, 3) : items;
  var moreItems = isTablet ? indexedItems.slice(3, items.length) : [];

  var Logo = function Logo() {
    if (theme === 'b2b') {
      return /*#__PURE__*/React.createElement(S7ProfiCircle, {
        size: "xl"
      });
    }

    if (theme === 'b2c') {
      return /*#__PURE__*/React.createElement(S7Circle, {
        size: "xl"
      });
    }

    return null;
  };

  var handleOpenMoreMenuClick = function handleOpenMoreMenuClick() {
    setIsOpenMore(true);
  };

  var handleCloseMoreMenuClick = function handleCloseMoreMenuClick() {
    setIsOpenMore(false);
  };

  var handleNavLinkClick = function handleNavLinkClick(item, event, isCustomOrAnchor) {
    if (isFunc(onItemClick) && !isCustomOrAnchor) {
      onItemClick(item.index);
    }

    handleCloseMoreMenuClick();

    if (item.onTitleClick) {
      if (isAnchorEvent(event)) {
        item.onTitleClick(event);
      } else if (isButtonEvent(event)) {
        item.onTitleClick(event);
      }
    }
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, isTablet && /*#__PURE__*/React.createElement("div", {
    className: cx('logo')
  }, Logo), /*#__PURE__*/React.createElement("ul", _extends({
    className: cx('root', "theme_".concat(theme), {
      is_search: searchOpen
    })
  }, getTestId()), navigationItems.map(function (_ref3, index) {
    var title = _ref3.title,
        _ref3$titleProps = _ref3.titleProps,
        titleProps = _ref3$titleProps === void 0 ? {} : _ref3$titleProps,
        href = _ref3.href,
        CustomComponent = _ref3.as,
        isDropDown = _ref3.isDropDown,
        insideItem = _ref3.items,
        target = _ref3.target,
        active = _ref3.active;
    var isActive = index === activeItem || active;
    var ref = isActive ? activeLinkRef : null;

    var props = _objectSpread({
      href: href,
      onClick: function onClick(event) {
        return handleNavLinkClick({
          index: index,
          onTitleClick: titleProps.onClick
        }, event, !!href || !!CustomComponent);
      },
      className: cx('point', classes.point),
      target: target
    }, getTestId('link'));

    var Component = function Component(_ref4) {
      var children = _ref4.children;
      return /*#__PURE__*/React.createElement(React.Fragment, null, href ? /*#__PURE__*/React.createElement("a", _extends({
        ref: ref
      }, titleProps, props), children) : /*#__PURE__*/React.createElement(ButtonBase, _extends({
        ref: ref
      }, titleProps, props), children));
    };

    if (isDropDown && theme === 'b2b') {
      return /*#__PURE__*/React.createElement(DropList, {
        title: title,
        index: index,
        insideItem: insideItem,
        namespace: namespace,
        isShowLayer: theme === 'b2b'
      });
    }

    return /*#__PURE__*/React.createElement("li", _extends({
      // eslint-disable-next-line react/no-array-index-key
      key: "nav-item-".concat(index),
      className: cx('item')
    }, getTestId(index.toString(), 'item')), CustomComponent ? /*#__PURE__*/React.createElement(CustomComponent, {
      ref: ref,
      title: title,
      titleProps: titleProps,
      href: href,
      className: cx('point', classes.point)
    }) : /*#__PURE__*/React.createElement(Component, null, /*#__PURE__*/React.createElement(Markup, {
      text: title
    })));
  }), /*#__PURE__*/React.createElement("li", {
    className: cx('marker', classes.marker),
    style: markerPosition
  }), isTablet && !!moreItems.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("li", {
    className: cx('item', 'point'),
    onClick: handleOpenMoreMenuClick,
    onKeyDown: handleOpenMoreMenuClick,
    role: "menuitem"
  }, /*#__PURE__*/React.createElement(DotsHorizontal, null)), /*#__PURE__*/React.createElement(Popover, {
    isOpen: isOpenMore,
    align: "right",
    onToggle: handleCloseMoreMenuClick
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('popup')
  }, /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    onClick: handleCloseMoreMenuClick,
    className: cx('button')
  }, getTestId('toggle', 'button')), /*#__PURE__*/React.createElement(CloseLinear, null)), moreItems.map(function (item) {
    return /*#__PURE__*/React.createElement("a", {
      key: item.index,
      href: item.href,
      onClick: function onClick(event) {
        return handleNavLinkClick(item, event);
      },
      className: cx('popup_link')
    }, /*#__PURE__*/React.createElement(Markup, {
      text: item.title
    }));
  }))))));
};
export default NavLinks;